/** Where the Account page lives, and where Settings opens (spec 239). */
export const ACCOUNT_SETTINGS_PATH = '/settings/account';

// Copy drafted by AI; awaiting the user's review (constitution).
/** The words of the Account page (spec 239). */
export const ACCOUNT_COPY = {
  title: 'Account',
  nav: 'Account',
  intro:
    'Who you are here, the accounts you have linked, and how you are signed in.',
  profileHeading: 'You',
  fromSignIn:
    "Your name and email come from your organization's sign-in, and change there.",
  orgAdmin: 'Organization admin',
  member: 'Member',
  openConsole: 'Open the admin console',
  linkedHeading: 'Linked accounts',
  linkedAs: (username: string, host: string) => `${username} on ${host}`,
  linkedNone:
    "No GitLab or GitHub account linked. Runs you start use the workspace's shared account.",
  linkedLoading: 'Loading your linked accounts…',
  linkedFailed: 'Your linked accounts could not be loaded.',
  manageLinked: 'Manage linked accounts',
  signInHeading: 'Sign-in',
  signedInAs: (email: string) => `Signed in as ${email}.`,
  switchAccount: 'Switch account',
  signOut: 'Sign out',
} as const;

/** Where the Commits settings page lives (spec 158). */
export const COMMITS_SETTINGS_PATH = '/settings/commits';

// Copy drafted by AI; reviewed and approved by the user 2026-10-12 (constitution).
/** The words of the Commits settings page (spec 158, contracts/ui.md). */
export const COMMITS_COPY = {
  title: 'Commits',
  nav: 'Commits',
  intro:
    'When the agent commits in a run you start, the commit names you as its author. Each commit links back to its run, and with a linked account below, the push and the merge request are yours too.',
  previewHeading: 'How your commits read',
  usesSignIn: 'Uses your sign-in email.',
  usesCommitEmail: 'Uses your commit email.',
  usesLinkedEmail: (forge: string) =>
    `Uses your linked ${forge} account's email.`,
  emailHeading: 'Commit email',
  emailLabel: 'Email address',
  emailHint:
    'Use the email your GitLab or GitHub account has, so the forge links your commits to your profile.',
  save: 'Save',
  saving: 'Saving…',
  useSignIn: 'Use my sign-in email',
  appliesFromNow: 'Changes apply to runs started from now on.',
  refused: "That isn't an email address.",
  saved: 'Commit email saved.',
  cleared: 'Commits now use your sign-in email.',
  saveFailed: 'Your commit email could not be saved.',
  loading: 'Loading your commit settings…',
  loadFailed: 'Your commit settings could not be loaded.',
  retry: 'Try again',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-12 (spec 159).
/** The words of the Linked accounts panel (spec 159, contracts/ui.md). */
export const FORGE_COPY = {
  heading: 'Linked accounts',
  intro:
    "Link your own GitLab or GitHub account, and runs you start push, open merge requests and comment as you. Without one, they use the workspace's shared account.",
  empty: 'No workspace connects to GitLab or GitHub yet.',
  loading: 'Loading your linked accounts…',
  loadFailed: 'Your linked accounts could not be loaded.',
  retry: 'Try again',
  notLinked: 'Not linked',
  notUsed: 'not used by any workspace',
  expires: (date: string) => `expires on ${date}`,
  link: 'Link',
  relink: 'Relink',
  replace: 'Replace token',
  unlink: 'Unlink',
  tokenLabel: 'Personal access token',
  show: 'Show token',
  hide: 'Hide token',
  createOne: (host: string) => `Create one on ${host}`,
  save: 'Link account',
  saving: 'Linking…',
  cancel: 'Cancel',
  refused: (forge: string) => `${forge} did not accept this token.`,
  unreachable: (forge: string) =>
    `${forge} could not be reached. Try again in a moment.`,
  linkFailed: 'The account could not be linked.',
  linked: (forge: string) => `${forge} account linked.`,
  linkedUnchecked: (forge: string) =>
    `${forge} account linked. Its permissions could not be checked.`,
  unlinkTitle: (forge: string) => `Unlink your ${forge} account?`,
  unlinkBody:
    "Runs you start will push and open merge requests with the workspace's shared account.",
  unlinked: (forge: string) => `${forge} account unlinked.`,
  unlinkFailed: 'The account could not be unlinked.',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 161).
/** Linking by signing in on the forge (spec 161, contracts/ui.md). */
export const SIGN_IN_COPY = {
  connect: (forge: string) => `Connect with ${forge}`,
  connecting: (forge: string) => `Opening ${forge}…`,
  tokenInstead: 'Use a personal access token instead',
  unavailable: (host: string) =>
    `Signing in is no longer set up for ${host}. Use a personal access token instead.`,
  notConnected: (host: string) => `No workspace connects to ${host} any more.`,
  failed: (forge: string) =>
    `Could not start signing in to ${forge}. Try again.`,
  dismiss: 'Dismiss',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 160).
/** Where a workspace requires each person's own account (spec 160, contracts/ui.md). */
export const PERSONAL_ACCOUNT_COPY = {
  link: 'Link account',
  relink: 'Relink account',
  notice: (hosts: string) =>
    `This workspace runs as each person's own account. Link yours for ${hosts} to start runs here.`,
  noticeLabel: 'Your own account is needed here',
  cardLabel: 'Needs account',
  canonicalName: 'Needs a personal account',
  runMark: 'needs own account',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-14 (spec 163).
/** The Commits page's preview and account cards (spec 163, contracts/ui.md). */
export const PREVIEW_COPY = {
  heading: 'Your next commit',
  accountsHeading: 'Your accounts',
  authored: (name: string) => `${name} authored`,
  committed: (committer: string) => `${committer} committed`,
  trailer: 'AI-Powered-SDLC-Run: a link to the run that made it',
  // Reworded in spec 170, approved 2026-10-14: "pushed as @jane, you" read backwards.
  pushedYou: (username: string) => `pushed as you (@${username})`,
  pushedShared: "pushed as the workspace's shared account",
  notLinked: (forge: string) => `You have not linked ${forge}.`,
  relink: 'Your link needs relinking.',
  requiredStops: (names: string) =>
    `Runs in ${names} won't start until it works.`,
  usedIn: (count: number) =>
    count === 1 ? 'Used in 1 workspace' : `Used in ${count} workspaces`,
  requiredIn: (names: string) => `required in ${names}`,
  requiredInCount: (count: number) =>
    count === 1 ? 'required in 1 workspace' : `required in ${count} workspaces`,
  notRequired: 'not required',
  workspaces: (count: number) =>
    count === 1 ? '1 workspace' : `${count} workspaces`,
  renews: 'Renews itself',
  permissionsOk: 'Has what runs need',
  permissionsUnknown: 'Its permissions could not be checked',
  emailUsing: {
    commit: 'using your commit email',
    linked: (forge: string) => `using your ${forge} account's email`,
    'sign-in': 'using your sign-in email',
  },
  justLinked: 'Just linked',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-14 (spec 170).
/** The preview's own way to link (spec 170). */
export const PREVIEW_LINK_COPY = {
  link: 'Link',
  relink: 'Relink',
  linkLabel: (forge: string, host: string) =>
    `Link your ${forge} account on ${host}`,
  relinkLabel: (forge: string, host: string) =>
    `Relink your ${forge} account on ${host}`,
} as const;
